import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { AutoAwesome, CloudUpload } from '@mui/icons-material';
import { aiBoqApi, projectsApi } from '../../services/benchmarkApi';
import { projectLabel } from './benchmarkFormat';

const BRAND = '#8B1538';

/** Must match backend BoqBidImportAgentResolver.STANDARD_AGENT_NAME */
export const BID_IMPORT_AGENT_NAME = 'BenchIQ Bid Import';

/** Agent validation.warnings/errors may be strings or objects — never render raw objects. */
const formatValidationMessage = (entry) => {
  if (entry == null) return '';
  if (typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean') {
    return String(entry).trim();
  }
  if (typeof entry === 'object') {
    const nested =
      entry.message ??
      entry.warning ??
      entry.error ??
      entry.detail ??
      entry.description ??
      entry.text ??
      entry.reason;
    if (nested != null && typeof nested !== 'object') {
      const code = entry.code != null ? String(entry.code).trim() : '';
      const msg = String(nested).trim();
      if (code && msg) return `${code}: ${msg}`;
      return msg || code;
    }
    try {
      return JSON.stringify(entry);
    } catch {
      return '';
    }
  }
  return String(entry);
};

/**
 * AI Bid Import: messy BOQ Excel → agent (by fixed name) → JSON → import as bids.
 */
const BenchmarkAiTransformDialog = ({ open, onClose, defaultProjectId, onSuccess }) => {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(defaultProjectId || '');
  const [file, setFile] = useState(null);
  const [contractorName, setContractorName] = useState('');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [transformResult, setTransformResult] = useState(null);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setProjectId(defaultProjectId || '');
    setFile(null);
    setContractorName('');
    setStep(1);
    setError('');
    setTransformResult(null);
    setImportResult(null);
    projectsApi
      .list()
      .then((res) => setProjects(res.data || []))
      .catch(() => setProjects([]));
  }, [open, defaultProjectId]);

  const doc = transformResult?.document;
  const costItems = doc?.cost_items || [];
  const contractors = useMemo(() => {
    const toName = (c) => {
      if (c == null) return '';
      if (typeof c === 'string' || typeof c === 'number') return String(c).trim();
      if (typeof c === 'object') {
        return String(c.contractor_name || c.name || c.contractor || '').trim();
      }
      return '';
    };
    const set = new Set();
    if (Array.isArray(doc?.contractors)) {
      doc.contractors.forEach((c) => {
        const name = toName(c);
        if (name) set.add(name);
      });
    }
    if (!set.size) {
      costItems.forEach((i) => {
        const name = toName(i?.contractor_name);
        if (name) set.add(name);
      });
    }
    return [...set];
  }, [doc, costItems]);

  const validation = doc?.validation || {};
  const warningMessages = useMemo(() => {
    const raw = validation.warnings;
    const list = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
    return list.map(formatValidationMessage).filter(Boolean);
  }, [validation.warnings]);
  const errorMessages = useMemo(() => {
    const raw = validation.errors;
    const list = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
    return list.map(formatValidationMessage).filter(Boolean);
  }, [validation.errors]);

  const handleTransform = async () => {
    if (!file) {
      setError('Choose an Excel/CSV file');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await aiBoqApi.transformBoq({
        file,
        contractorName: contractorName || undefined,
      });
      setTransformResult(res.data);
      setStep(2);
    } catch (e) {
      setError(e.message || 'Transform failed');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!projectId) {
      setError('Select a project');
      return;
    }
    if (!doc || !costItems.length) {
      setError('No cost items to import');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await aiBoqApi.importJson({
        document: doc,
        projectId,
      });
      setImportResult(res.data);
      setStep(3);
      if (onSuccess) onSuccess(res.data);
    } catch (e) {
      setError(e.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesome sx={{ color: BRAND }} />
        Upload with AI
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {step === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Excel → agent <strong>{BID_IMPORT_AGENT_NAME}</strong> → JSON → save as bids.
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Project</InputLabel>
              <Select
                label="Project"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              >
                {projects.map((p) => (
                  <MenuItem key={p.projectId} value={p.projectId}>
                    {projectLabel(p)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Contractor filter (optional)"
              value={contractorName}
              onChange={(e) => setContractorName(e.target.value)}
              fullWidth
              helperText="e.g. ACC — only extract one bidder"
            />
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
            >
              {file ? file.name : 'Choose Excel / CSV'}
              <input
                hidden
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </Button>
          </Box>
        )}

        {step === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2">
              Cost items: <strong>{costItems.length}</strong>
              {' · '}
              Contractors: <strong>{contractors.length}</strong>
              {' · '}
              Status: <strong>{validation.status || '—'}</strong>
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {contractors.map((c) => (
                <Chip key={c} size="small" label={String(c)} variant="outlined" />
              ))}
            </Box>
            {!!warningMessages.length && (
              <Alert severity="warning" sx={{ alignItems: 'flex-start' }}>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {warningMessages.slice(0, 8).map((msg, idx) => (
                    <li key={`warn-${idx}`}>
                      <Typography variant="body2" component="span">
                        {msg}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
            {!!errorMessages.length && (
              <Alert severity="error" sx={{ alignItems: 'flex-start' }}>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {errorMessages.slice(0, 8).map((msg, idx) => (
                    <li key={`err-${idx}`}>
                      <Typography variant="body2" component="span">
                        {msg}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
            {!costItems.length && (
              <Alert severity="warning">No items returned by the agent.</Alert>
            )}
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel>Import into project</InputLabel>
              <Select
                label="Import into project"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              >
                {projects.map((p) => (
                  <MenuItem key={p.projectId} value={p.projectId}>
                    {projectLabel(p)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {step === 3 && importResult && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Alert severity={importResult.failedContractors ? 'warning' : 'success'}>
              Imported {importResult.importedContractors} contractor batch(es)
              {importResult.failedContractors
                ? ` · ${importResult.failedContractors} failed`
                : ''}
            </Alert>
            {(importResult.errors || []).map((err, idx) => (
              <Typography key={idx} variant="caption" color="error">
                {typeof err === 'string' ? err : err?.message || JSON.stringify(err)}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {step === 3 ? 'Close' : 'Cancel'}
        </Button>
        {step === 1 && (
          <Button
            variant="contained"
            onClick={handleTransform}
            disabled={loading || !file}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
            sx={{ bgcolor: BRAND, '&:hover': { bgcolor: '#6B0F2A' } }}
          >
            Transform with AI
          </Button>
        )}
        {step === 2 && (
          <>
            <Button onClick={() => setStep(1)} disabled={loading}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={loading || !costItems.length || !projectId}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ bgcolor: BRAND, '&:hover': { bgcolor: '#6B0F2A' } }}
            >
              Import as bids
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BenchmarkAiTransformDialog;
